import { createContext, useContext, useRef, useState } from "react";

export const DashboardContext = createContext(null)

export function DashboardProvider({ children }) {

    const scrollContainerRef = useRef(null) // we use this to scroll to top of the page when needed, that way we have global access to it.

    /* Integrations */
    const [integrations, setIntegrations] = useState([])
    const updateIntegrationsState = (integrationsToUpdateArray) => {
        setIntegrations([...integrations, ...integrationsToUpdateArray])
    }
    const updateExistingIntegrationState = (integrationId, newData) => {
        setIntegrations(integrations.map(i => i._id === integrationId ? newData : i))
    }
    const removeIntegrationFromState = (integrationId) => {
        setIntegrations(integrations.filter(i => i._id !== integrationId))
    }

    /* Notifications */
    const [notifications, setNotifications] = useState([])
    const addNotifications = (newNotifications) => {
        setNotifications([...newNotifications, ...notifications])
    }
    const removeNotification = (notificationId) => {
        setNotifications(notifications.filter(n => n._id !== notificationId))
    }

    /* Creatives */
    const [creativesMap, setCreativesMap] = useState({})
    const getAllCreativesFromState = () => Object.values(creativesMap)
    const getAllTopLevelCreativesFromState = () => getAllCreativesFromState().filter(c => !c.folder)
    const getCreativeFromState = (creativeId) => creativesMap[creativeId]
    const updateCreativeStateById = (creativeId, newData) => {
        const newSate = JSON.parse(JSON.stringify(creativesMap))
        newSate[creativeId] = newData
        setCreativesMap(newSate)
    }
    const removeManyCreativesFromState = (creativeIds) => {
        const newSate = JSON.parse(JSON.stringify(creativesMap))
        for (const creativeId of creativeIds) {
            delete newSate[creativeId]
        }
        setCreativesMap(newSate)
    }
    const removeCreativeFromState = (creativeId) => {
        const newSate = JSON.parse(JSON.stringify(creativesMap))
        delete newSate[creativeId]
        setCreativesMap(newSate)
    }
    const updateCreativeState = (creatives) => {
        const newSate = JSON.parse(JSON.stringify(creativesMap))
        for (const creative of creatives) {
            newSate[creative._id] = creative
        }
        setCreativesMap(newSate)
    }
    const getCreativesOfFolderFromState = (folderId) => getAllCreativesFromState().filter(c => c.folder === folderId)
    const getAllCreativesForAdAccount = (adAccountId) => getAllCreativesFromState().filter(c => c.adAccountId === adAccountId)
    const getAllPropertiesForThisAdAccount = (adAccountId) => {

        const allCreatives = getAllCreativesForAdAccount(adAccountId)
        const allPropertiesMap = {}
        const tagMap = {}

        for (const creative of allCreatives) {
            if (creative.properties) {
                for (const property of creative.properties) {
                    const propertyLabel = property.label?.toLowerCase()
                    if (propertyLabel) {
                        if (!allPropertiesMap[propertyLabel]) {
                            allPropertiesMap[propertyLabel] = { label: property.label, tags: [] }
                        }
                        if (property.tags) {
                            for (const tag of property.tags) {
                                const key = `${propertyLabel}_${tag.value}`
                                if (!tagMap[key]) {
                                    tagMap[key] = true
                                    allPropertiesMap[propertyLabel].tags.push(tag)
                                }
                            }
                        }
                    }
                }
            }
        }

        return allPropertiesMap

    }

    /* Folders */
    const [foldersMap, setFoldersMap] = useState({})
    const getAllFoldersFromState = () => Object.values(foldersMap)
    const getTopLevelFoldersFromState = () => getAllFoldersFromState().filter(f => !f.parent)
    const getFolderChildrenFromState = (folderId) => getAllFoldersFromState().filter(f => f.parent === folderId)
    const getFolderFromState = (folderId) => foldersMap[folderId]
    const updateFolderStateById = (folderId, newData) => {
        const newSate = JSON.parse(JSON.stringify(foldersMap))
        newSate[folderId] = newData
        setFoldersMap(newSate)
    }
    const removeFolderFromState = (folderId) => {
        const newSate = JSON.parse(JSON.stringify(foldersMap))
        delete newSate[folderId]
        setFoldersMap(newSate)
    }
    const updateFolderState = (folders) => {
        const newSate = JSON.parse(JSON.stringify(foldersMap))
        for (const folder of folders) {
            newSate[folder._id] = folder
        }
        setFoldersMap(newSate)
    }

    /* Creative Reports */
    const [creativeReports, setCreativeReports] = useState([])
    const getCreativeReportFromState = (creativeId) => creativeReports.find(c => c._id === creativeId)
    const updateCreativeReportState = (creativeReport) => {
        const newState = creativeReports.map(c => c._id === creativeReport._id ? creativeReport : c)
        setCreativeReports(newState)
    }
    const addManyCreativeReportsToState = (creativeReportsToUpdate) => {
        setCreativeReports([...creativeReports, ...creativeReportsToUpdate])
    }
    const removeCreativeReportFromState = (creativeId) => {
        setCreativeReports(creativeReports.filter(c => c._id !== creativeId))
    }

    /* General (not sure yet where to put it) */
    const [chosenAdAccount, setChosenAdAccount] = useState(null)

    return (
        <DashboardContext.Provider
            value={{
                creativeContext: {
                    setCreativesMap, getCreativeFromState, updateCreativeState, updateCreativeStateById, getCreativesOfFolderFromState,
                    getAllCreativesFromState, removeManyCreativesFromState, getAllTopLevelCreativesFromState,
                    setFoldersMap, updateFolderStateById, getFolderFromState, removeFolderFromState, updateFolderState, getAllFoldersFromState, getTopLevelFoldersFromState, getFolderChildrenFromState, removeCreativeFromState,
                    creativeReports, getCreativeReportFromState, updateCreativeReportState, addManyCreativeReportsToState, removeCreativeReportFromState, setCreativeReports,
                    getAllPropertiesForThisAdAccount
                },
                integrationsContext: { integrations, updateIntegrationsState, updateExistingIntegrationState, removeIntegrationFromState },
                notificationsContext: { notifications, addNotifications, removeNotification },
                adAccountsContext: { chosenAdAccount, setChosenAdAccount },
                scrollContext: { scrollContainerRef }
            }}
        >
            {children}
        </DashboardContext.Provider>
    )
}

export const useDashboardContext = () => {
    const context = useContext(DashboardContext);

    if (!context) throw new Error('useDashboardContext context must be use inside DashboardProvider');

    return context;
}

